import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, PaperPlaneRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Messages() {
  const { id } = useParams();
  const { user } = useAuth();
  const [other, setOther] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const bottomRef = useRef(null);

  const load = async () => {
    try {
      const [, ms] = await Promise.all([
        api.get(`/speakers/${id}`).catch(() => ({ data: null })),
        api.get(`/networking/messages/${id}`),
      ]);
      setMessages(ms.data);
      // Fire-and-forget: mark every message from this person as read so the
      // Network nav badge clears next time the Layout polls.
      api.post(`/networking/messages/${id}/mark-read`).catch(() => {});
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { if (user) { load(); loadOther(); } /* eslint-disable-next-line */ }, [id, user]);

  const loadOther = async () => {
    try {
      const atts = await api.get("/networking/attendees").catch(() => ({ data: [] }));
      const found = atts.data.find((a) => a.id === id);
      setOther(found || { id, name: "Delegate", role_title: "", org: "", photo_url: "" });
    } catch (err) { console.warn("[messages] loadOther failed:", err); }
  };

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const optimistic = { id: `tmp-${Date.now()}`, from_id: user.id, to_id: id, text, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, optimistic]);
    setText("");
    try {
      const { data } = await api.post("/networking/messages", { to_user_id: id, text: optimistic.text });
      setMessages((prev) => prev.map((m) => m.id === optimistic.id ? data : m));
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="messages-page" className="flex flex-col min-h-[calc(100vh-12rem)]">
      <Link to="/networking" className="inline-flex items-center gap-1 text-sm text-[#5a5a5a] hover:text-[#000000] mb-3">
        <ArrowLeft size={16} /> Back
      </Link>
      {other && (
        <div className="flex items-center gap-3 pb-3 border-b border-[#E5E9F0] mb-3">
          <div className="w-10 h-10 rounded-sm bg-[#E5E9F0] overflow-hidden">
            {other.photo_url ? <img src={other.photo_url} alt={other.name} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-[#7A828C]">{other.name[0]}</div>}
          </div>
          <div>
            <div className="font-medium">{other.name}</div>
            <div className="text-xs text-[#7A828C]">{other.role_title}{other.org ? ` · ${other.org}` : ""}</div>
          </div>
        </div>
      )}

      <div className="flex-1 space-y-2 pb-4" data-testid="messages-list">
        {messages.length === 0 && <div className="text-center text-[#7A828C] py-8 text-sm">Say hello 👋</div>}
        {messages.map((m) => {
          const mine = m.from_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-sm px-3 py-2 text-sm ${mine ? "bg-[#000000] text-[#FFFFFF]" : "bg-[#FFFFFF] border border-[#E5E9F0]"}`}>
                {m.text}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={send} className="sticky bottom-20 bg-[#FFFFFF] py-2 flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…"
          className="flex-1 h-12 px-3 rounded-sm bg-[#FFFFFF] border border-[#E5E9F0] focus:border-[#000000] outline-none" data-testid="message-input" />
        <Button type="submit" className="h-12 rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]" data-testid="message-send-btn">
          <PaperPlaneRight size={18} />
        </Button>
      </form>
    </div>
  );
}
